import * as React from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  type ChartWindow,
  sliceMonths,
  tierMixSeries,
  windowCoverage,
} from '../../../../../shared/riskregister/executiveCharts';
import type { Payload, Row, Tier } from '../../../../../shared/riskregister/types';
import { ChartWindowSelect } from './ChartWindowSelect';
import '../../executiveCharts.css';

const TIER_COLORS: Record<Tier, string> = {
  critical: 'var(--accent-red)',
  high: 'var(--accent-amber)',
  moderate: 'var(--accent-blue)',
  unscored: 'var(--text-muted)',
};

function share(count: number, total: number): number {
  return total ? Math.round((count / total) * 100) : 0;
}

export const RiskMixChart: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
}> = ({ payload, rows }) => {
  const [chartWindow, setChartWindow] = React.useState<ChartWindow>('365d');
  const allMonths = React.useMemo(() => tierMixSeries(rows, payload), [payload, rows]);
  const series = React.useMemo(
    () => sliceMonths(allMonths, chartWindow, payload.as_of),
    [allMonths, payload.as_of, chartWindow],
  );
  const coverage = React.useMemo(
    () => windowCoverage(allMonths, chartWindow, payload.as_of),
    [allMonths, payload.as_of, chartWindow],
  );

  if (!rows.length || !series.length) {
    return <p className="risk-register__empty">No month-end history is available for this selection.</p>;
  }

  const last = series[series.length - 1];
  const first = series[0];
  const severeNow = last.critical + last.high;
  const severeThen = first.critical + first.high;
  const description = series
    .map(
      (point) =>
        `${point.label}: ${payload.tier_order
          .map((tier) => `${payload.tiers[tier].short} ${point[tier]}`)
          .join(', ')}, ${point.total} open in total`,
    )
    .join('. ');

  return (
    <div className="risk-register__chart-panel">
      <ChartWindowSelect
        id="risk-register-risk-mix-window"
        value={chartWindow}
        onChange={setChartWindow}
        coverage={coverage}
      />

      <p className="risk-register__chart-verdict">
        <span className="risk-register__chart-figures">
          Critical and high items are {share(severeNow, last.total)}% of open risk at {last.label}, against{' '}
          {share(severeThen, first.total)}% at {first.label}.
        </span>
      </p>

      <div
        className="risk-register__chart"
        role="img"
        aria-label={`Open risk by tier at each month end. ${description}`}
      >
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={series} margin={{ top: 16, right: 24, left: 0, bottom: 4 }} stackOffset="expand">
            <CartesianGrid stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              minTickGap={16}
            />
            <YAxis
              tickFormatter={(value: number) => `${Math.round(value * 100)}%`}
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            />
            <Legend />
            {payload.tier_order.map((tier) => (
              <Area
                key={tier}
                type="monotone"
                dataKey={tier}
                stackId="tier"
                name={payload.tiers[tier].label}
                stroke={TIER_COLORS[tier]}
                fill={TIER_COLORS[tier]}
                fillOpacity={0.55}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className="risk-register__chart-caveat">
        Each item is counted at the tier it holds today, applied back over past months. Tier is not rebuilt from
        history, so the mix for earlier months shows the shape of the change, not exact figures.
      </p>
    </div>
  );
};
